import {
  addSeconds,
  differenceInSeconds,
  format as dateFnsFormat,
  formatRelative,
  getUnixTime,
  isBefore,
  isWithinInterval,
} from 'date-fns';
import { nb } from 'date-fns/locale';

import { captureExceptionInSentry } from '../errorTracker/tracking';
import { specialcharacters } from '../special-characters/special-characters.utils';

/**
 * @description Format date with nb locale
 */
export const formatDate = (date: Date, formatString: string) => {
  return dateFnsFormat(date, formatString, { locale: nb });
};

export const toDate = (date: string | Date): Date => {
  if (date instanceof Date) {
    return date;
  }
  return new Date(date);
};

export const relativeTimeFromNow = (date: Date) => {
  return formatRelative(date, new Date(), { locale: nb });
};

export const getYearFormatted = (): string => formatDate(new Date(), 'yyyy');
export const getSecondsBetweenDates = (date1: Date, date2: Date): number => {
  return differenceInSeconds(date1, date2);
};
export const getUnix = (date: string | Date): number => getUnixTime(toDate(date));

export const formatTime = (date: string | Date): string => formatDate(toDate(date), 'HH:mm');

export const getDateFormattedShort = (date: string | Date | undefined): string =>
  date ? formatDate(toDate(date), `d.${specialcharacters.nbsp}MMMM`) : '';

export const getDateFormattedLong = (date: string | Date): string => formatDate(toDate(date), 'd. MMMM yyyy');

export const getDateSortable = (date: string | Date): string => formatDate(toDate(date), 'yyyy-MM-dd');

export const getAnalyticsDate = (date: string | Date): string => formatDate(toDate(date), 'dd-MM-yyyy HH:mm:ss');

export const getStartEndTime = (
  startDate: string | Date,
  duration: number
): {
  start: Date;
  end: Date;
} => {
  const startTime = toDate(startDate);
  const endTime = addSeconds(startTime, duration);
  return {
    start: startTime,
    end: endTime,
  };
};

export const isDateInRange = (currentDate: string | Date, startDate: string | Date, duration: number): boolean => {
  currentDate = toDate(currentDate);
  startDate = toDate(startDate);
  const endDate = addSeconds(startDate, duration);

  if (isBefore(endDate, startDate)) {
    captureExceptionInSentry(new Error(`End of interval is before start. startDate: ${startDate} endDate: ${endDate}`));
    return false;
  }

  return isWithinInterval(currentDate, { start: startDate, end: endDate });
};

export const isDateBetweenDates = (
  currentDate: string | Date,
  startDate: string | Date,
  endDate: string | Date
): boolean => {
  return isWithinInterval(toDate(currentDate), { start: toDate(startDate), end: toDate(endDate) });
};

export const getRatioOfCurrentTimeInRange = (startDate: string | Date, endDate: string | Date): number => {
  const nowUnix = new Date().getTime() / 1000;
  const startUnix = getUnix(toDate(startDate));
  const endUnix = getUnix(toDate(endDate));
  const ratio = (nowUnix - startUnix) / (endUnix - startUnix);
  const ratioRounded = Math.round(ratio * 100) / 100;
  return ratioRounded;
};
